'use client';
// EmptyState Component - Pure MUI
import InboxIcon from '@mui/icons-material/Inbox';
import { Box, Button, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useT } from '@/hooks';

interface EmptyStateProps {
  title?: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ReactNode;
}

export function EmptyState({ title, description, actionLabel, onAction, icon }: EmptyStateProps) {
  const { t } = useT();

  const finalTitle = title ?? t('common.noDataFound', { defaultValue: 'No data found' });
  const finalDesc =
    description ??
    t('common.noDataToDisplay', { defaultValue: 'There is no data to display at the moment.' });
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        {icon || <InboxIcon sx={{ fontSize: 32, color: 'primary.main' }} />}
      </Box>
      <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
        {finalTitle}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 320 }}>
        {finalDesc}
      </Typography>
      {actionLabel && onAction && (
        <Button
          variant="contained"
          onClick={onAction}
          sx={{
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.75),
            color: 'primary.contrastText',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'primary.main',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: 'none',
            },
          }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}

export default EmptyState;
